import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    alpha,
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SurfaceSection from "../Common/SurfaceSection";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { YearComparisonReport } from "../../types/Reports";
import LineChart from "../Common/Charts/LineChart";
import Legend from "../Legend";
import { formatNumber, formatPercentage } from "../../utils/formatNumber";
import { Bar } from "react-chartjs-2";
import ProgressBar from "../progressBar/ProgressBar";
import useReportsService from "../../services/useReportsService";
import { getMonthsShort } from "../../utils/time";

const CloseButton = styled(IconButton)(({ theme }) => `
    color: ${theme.palette.primary[100]};
    position: absolute;
    right: ${theme.spacing(2)};
    top: ${theme.spacing(2)};
`);

type YearComparisonsReportDialogProps = { open: boolean; onClose: () => void };

const YearComparisonsReportDialog = ({ open, onClose }: YearComparisonsReportDialogProps) => {
    const theme = useTheme();
    const { t } = useTranslation("common");
    const monthsShort = useMemo(() => getMonthsShort(t), [t]);
    const isTabletOrMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isSmallPhone = useMediaQuery('(max-width:380px)');

    const { fetchYearComparisonReportData } = useReportsService();

    const currentYear = useMemo(() => new Date().getFullYear(), []);
    const currentMonth = useMemo(() => new Date().getMonth() + 1, []); // 1-indexed

    const [data, setData] = useState<YearComparisonReport | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [referenceYear, setReferenceYear] = useState<number>(currentYear);
    const [availableYears, setAvailableYears] = useState<number[]>([currentYear]);

    const lineParentRef = useRef<HTMLDivElement>(null);
    const donutParentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        setIsLoading(true);
        // todo do this like the other reports with toast
        fetchYearComparisonReportData(referenceYear)
            .then((data) => {
                setData(data);
                if (referenceYear === currentYear && data?.yearsTotals) {
                    const years = Object.keys(data.yearsTotals).map(Number).sort((a, b) => b - a);
                    const minYear = Math.min(...years);
                    setAvailableYears(years.filter(y => y !== minYear));
                }
            })
            .catch(() => setError(t("common.unknownError")))
            .finally(() => setIsLoading(false));
    }, [open, referenceYear, fetchYearComparisonReportData, t]);

    const noData = useMemo(() => {
        if (!data) return true;

        return !(Object.keys(data.monthsThisYearAndLastYear).length > 1 && Object.keys(data.yearsTotals).length > 1);
    }, [data]);

    // todo check this implementation and the charts
    const { lastYearMonthly, thisYearMonthly, thisYearEstimatedMonthly } = useMemo(() => {
        const empty = {
            thisYearMonthly: [],
            lastYearMonthly: [],
            thisYearEstimatedMonthly: [],
        };
        if (noData || !data) return empty;

        // Find the max month available for this year and last year
        const currentYearData = data.monthsThisYearAndLastYear[referenceYear];
        const lastYearData = data.monthsThisYearAndLastYear[referenceYear - 1];

        const thisYearMonths = currentYearData ? Object.keys(currentYearData).map(Number) : [];
        const lastYearMonths = lastYearData ? Object.keys(lastYearData).map(Number) : [];
        const maxThisYearMonth = thisYearMonths.length > 0 ? Math.max(...thisYearMonths) : 0;
        const maxLastYearMonth = lastYearMonths.length > 0 ? Math.max(...lastYearMonths) : 0;
        const maxMonth = Math.max(maxThisYearMonth, maxLastYearMonth, 0);

        // Fill monthly arrays only up to the last available month
        const thisYearArr = new Array(maxMonth).fill(null);
        const lastYearArr = new Array(maxMonth).fill(null);

        if (currentYearData) {
            Object.keys(currentYearData).forEach(month => {
                const monthNum = parseInt(month);
                const monthIndex = monthNum - 1; // Convert 1-12 to 0-11
                if (monthIndex < maxMonth) {
                    // Exclude the current (incomplete) month from the solid series (only for current year)
                    thisYearArr[monthIndex] = (referenceYear === currentYear && monthNum === currentMonth) ? null : currentYearData[monthNum];
                }
            });
        }

        if (lastYearData) {
            Object.keys(lastYearData).forEach(month => {
                const monthIndex = parseInt(month) - 1; // Convert 1-12 to 0-11
                if (monthIndex < maxMonth) {
                    lastYearArr[monthIndex] = lastYearData[parseInt(month)];
                }
            });
        }

        const estimatedArr = new Array(maxMonth).fill(null);
        if (referenceYear === currentYear) {
            const currentMonthIndex = currentMonth - 1;
            const estimatedValue = currentYearData?.[currentMonth];
            if (estimatedValue !== undefined && estimatedValue > 0 && currentMonthIndex < maxMonth) {
                if (currentMonthIndex > 0) estimatedArr[currentMonthIndex - 1] = thisYearArr[currentMonthIndex - 1];
                estimatedArr[currentMonthIndex] = estimatedValue;
            }
        }

        return {
            thisYearMonthly: thisYearArr,
            lastYearMonthly: lastYearArr,
            thisYearEstimatedMonthly: estimatedArr,
        };
    }, [noData, data, referenceYear, currentYear, currentMonth]);

    const renderTopLineChart = () => {
        if (noData) return null;
        return (
            <LineChart
                parentRef={lineParentRef}
                colorPalette={[theme.palette.primary.light, theme.palette.primary.light, theme.palette.primary.dark]}
                topBottomPadding={isSmallPhone ? 8 : 16}
                leftRightPadding={isSmallPhone ? 8 : 16}
                data={{
                    xAxis: {
                        data: monthsShort,
                        scaleType: 'point',
                    },
                    series: [
                        {
                            id: 'thisYear',
                            label: t("reports.currentYearVsPreviousYears.thisYear"),
                            curve: 'linear',
                            showMark: !isSmallPhone,
                            area: true,
                            data: thisYearMonthly,
                            valueFormatter: (value, context) =>
                                referenceYear === currentYear && context.dataIndex === currentMonth - 1 ? null : formatNumber(value ?? 0),
                        },
                        {
                            id: 'thisYearEstimate',
                            label: t("reports.currentYearVsPreviousYears.estimatedCurrentMonth"),
                            curve: 'linear',
                            showMark: !isSmallPhone,
                            area: false,
                            dashed: true,
                            data: thisYearEstimatedMonthly,
                            // show estimate only for current month of current year
                            valueFormatter: (value, context) =>
                                referenceYear === currentYear && context.dataIndex === currentMonth - 1 && value ? formatNumber(value) : null,
                        },
                        {
                            id: 'lastYear',
                            label: t("reports.currentYearVsPreviousYears.lastYear"),
                            curve: 'linear',
                            showMark: !isSmallPhone,
                            area: false,
                            data: lastYearMonthly,
                        },
                    ],
                    legendItems: !isSmallPhone ? [
                        { color: theme.palette.primary.dark, label: t("reports.currentYearVsPreviousYears.lastYear") },
                        { color: theme.palette.primary.light, label: t("reports.currentYearVsPreviousYears.thisYear") },
                        { color: theme.palette.primary.light, label: t("reports.currentYearVsPreviousYears.estimatedCurrentMonth"), dashed: true },
                    ] : undefined,
                }}
            />
        );
    };

    const renderYTDDonut = () => {
        if (!data) return null;
        const currentYearTotals = data?.yearsTotals[referenceYear];
        if (!currentYearTotals) return null;

        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                }}
            >
                <ProgressBar
                    sx={{ maxWidth: 120 }}
                    defaultPercentageValue={currentYearTotals.percentDiffToPrevYear}
                    progressBars={[
                        {
                            current: currentYearTotals?.realValue ?? 0,
                            max: currentYearTotals?.maxValue ?? 0,
                            color: theme.palette.primary.light,
                        },
                        {
                            current: currentYearTotals?.prevYearAtThisTime ?? 0,
                            max: currentYearTotals?.maxValue ?? 0,
                            color: theme.palette.primary.dark,
                        },
                    ]}
                    titleBars={[
                        t("reports.currentYearVsPreviousYears.thisYear"),
                        t("reports.currentYearVsPreviousYears.lastYear"),
                    ]}
                    showPercentage={true}
                />
            </Box>
        );
    };

    const renderYearlyBar = () => {
        if (noData || !data) return null;

        const yearEntries = Object.entries(data.yearsTotals)
            .map(([key, val]) => ({
                year: Number(key),
                realValue: val.realValue ?? 0,
                newCustomers: val.newCustomers ?? 0,
                estimatedValue: val.estimatedValue ?? 0,
            }));

        if (yearEntries.length === 0) return null;

        const years = yearEntries.map(e => e.year);
        const realValues = yearEntries.map(e => e.realValue);
        const newCustomers = yearEntries.map(e => e.newCustomers);
        const returningCustomers = yearEntries.map(e => Math.max(0, e.realValue - e.newCustomers));
        const estimatedValues = yearEntries.map(e => e.estimatedValue);

        return (
            <Box sx={{ flex: 1, width: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                {!isSmallPhone && (
                    <Box>
                        <Legend
                            items={[
                                { color: theme.palette.success.main, label: t("reports.yearComparisonsReport.barChart.newClients") },
                                { color: theme.palette.primary.main, label: t("reports.yearComparisonsReport.barChart.clients") },
                                { color: theme.palette.primary.dark, label: t("reports.yearComparisonsReport.barChart.estimation") },
                            ]}
                        />
                    </Box>
                )}
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    <Bar
                        data={{
                            labels: years.map(String),
                            datasets: [
                                {
                                    label: t("reports.yearComparisonsReport.barChart.newClients"),
                                    data: newCustomers,
                                    backgroundColor: alpha(theme.palette.success.main, 0.6),
                                },
                                {
                                    label: t("reports.yearComparisonsReport.barChart.clients"),
                                    data: returningCustomers,
                                    backgroundColor: theme.palette.primary.dark,
                                },
                                {
                                    label: t("reports.yearComparisonsReport.barChart.estimation"),
                                    data: estimatedValues,
                                    backgroundColor: alpha(theme.palette.primary.dark, 0.4),
                                },
                            ],
                        }}
                        style={{ width: '100%', height: '100%' }}
                        options={{
                            maintainAspectRatio: false,
                            scales: {
                                y: {
                                    beginAtZero: true,
                                    stacked: true,
                                    // todo this should be included in the wrapper component
                                    ticks: {
                                        color: theme.palette.text.secondary,
                                        font: {
                                            size: isSmallPhone ? 9 : 12,
                                        }
                                    },
                                    grid: { color: theme.palette.divider }
                                },
                                // todo this should be included in the wrapper component
                                x: {
                                    stacked: true,
                                    ticks: {
                                        color: theme.palette.text.secondary,
                                        font: {
                                            size: isSmallPhone ? 9 : 12,
                                        }
                                    },
                                    grid: { color: theme.palette.divider }
                                },
                            },
                            plugins: {
                                tooltip: {
                                    callbacks: {
                                        label: () => {
                                            return "";
                                        },
                                        footer: (items) => {
                                            const index = items[0].dataIndex;
                                            const realClients = realValues[index];
                                            const newClients = newCustomers[index];
                                            const newClientsPercentage = realClients > 0
                                                ? (newClients / realClients) * 100
                                                : 0;

                                            return [
                                                `${t("reports.yearComparisonsReport.barChart.totalClients")}: ${formatNumber(realClients)}`,
                                                `${t("reports.yearComparisonsReport.barChart.newClients")}: ${formatPercentage(newClientsPercentage, false)}`,
                                            ];
                                        },
                                    },
                                },
                                legend: { display: false },
                            },
                        }}
                    />
                </Box>
            </Box>
        );
    };

    // todo this is all very similar between reports
    // we should have a general report compoenent and each report sends the callback call
    // render content function (charts tables etc)
    const renderContent = () => {
        if (error) {
            return (
                <Box display="flex" justifyContent="center" my={2}>
                    <Typography variant="h6" align="center" width="100%" color="error">
                        {error}
                    </Typography>
                </Box>
            );
        }

        if (isLoading) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
                    <CircularProgress />
                </Box>
            );
        }

        if (noData) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
                    <Typography variant="h6" align="center" width="100%">
                        {t("reports.common.noData")}
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center' }}>
                <SurfaceSection
                    ref={lineParentRef}
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flex: isTabletOrMobile ? 5 : 6,
                        justifyContent: 'center',
                        minHeight: 0,
                    }}
                >
                    {renderTopLineChart()}
                </SurfaceSection>
                <Box sx={{ flex: isTabletOrMobile ? 7 : 5, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <Box display="flex" gap={2} flexDirection={isTabletOrMobile ? 'column' : 'row'} sx={{ flex: 1, minHeight: 0 }}>
                        <SurfaceSection
                            ref={donutParentRef}
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                flex: isTabletOrMobile ? 1 : 4,
                                justifyContent: 'center',
                                minHeight: 0,
                                overflow: 'hidden',
                                px: isSmallPhone ? 0.5 : 1,
                            }}>
                            {renderYTDDonut()}
                        </SurfaceSection>
                        <SurfaceSection
                            sx={{
                                display: 'flex',
                                flex: isTabletOrMobile ? 1 : 8,
                                flexDirection: 'column',
                                minHeight: 0,
                                p: isSmallPhone ? 0.5 : 1,
                            }}>
                            {renderYearlyBar()}
                        </SurfaceSection>
                    </Box>
                </Box>
            </Box>
        )
    };

    return (
        <Dialog open={open} onClose={onClose} fullScreen>
            {/* todo this should be general */}
            <DialogTitle sx={{ pr: isSmallPhone ? 7 : 9 }}>
                {t("reports.currentYearVsPreviousYears.title")}
                <CloseButton aria-label="close" onClick={onClose}>
                    <CloseIcon />
                </CloseButton>
            </DialogTitle>
            <DialogContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                {/* todo this should be general */}
                <Box sx={{ mb: 2, px: isSmallPhone ? 0.5 : 0, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant={isSmallPhone ? 'body2' : 'body1'} sx={{ flex: 1 }}>
                        {t("reports.currentYearVsPreviousYears.subtitle")}
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                            value={referenceYear}
                            onChange={(e) => setReferenceYear(Number(e.target.value))}
                        >
                            {availableYears.map((year) => (
                                <MenuItem key={year} value={year}>{year}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                {renderContent()}
            </DialogContent>
        </Dialog>
    );
}

export default YearComparisonsReportDialog;
